import React, { useState, useEffect } from 'react';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
    CircularProgress,
    Typography,
    Divider,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
    createVehicle,
    updateVehicle,
    getBranches,
    Vehicle,
    CreateVehicleData,
    UpdateVehicleData,
} from '../../store/slices/vehicleSlice';
import GenericModal from '../../components/GenericModal';
import GenericButton from '../../components/GenericButton';

interface VehicleFormProps {
    open: boolean;
    vehicle?: Vehicle | null;
    onClose: () => void;
    onSuccess: () => void;
}

const VehicleForm: React.FC<VehicleFormProps> = ({ open, vehicle, onClose, onSuccess }) => {
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useDispatch<AppDispatch>();
    const { branches, loading, error } = useSelector((state: RootState) => state.vehicles);

    // Form state
    const [formData, setFormData] = useState<CreateVehicleData>({
        branch_id: 0,
        vehicle_number: '',
        type_of_vehicle: '',
        make: '',
        model: '',
        unladen_weight: '',
        capacity_in_unit: '',
        capacity_in_weight: '',
        capacity_in_volume: '',
        chasis_number: '',
        engine_number: '',
        marker_name: '',
        registration_number: '',
        puc_validity: '',
        insurance_validity: '',
        ownership: '',
        rented_from: '',
        rented_to: '',
        owner_name: '',
        transporter: '',
        financer: '',
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load branches when modal opens
    useEffect(() => {
        if (open) {
            dispatch(getBranches());
        }
    }, [open, dispatch]);

    // Initialize form with vehicle data if editing
    useEffect(() => {
        if (vehicle) {
            setFormData({
                branch_id: vehicle.branch_id,
                vehicle_number: vehicle.vehicle_number,
                type_of_vehicle: vehicle.type_of_vehicle || '',
                make: vehicle.make || '',
                model: vehicle.model || '',
                unladen_weight: vehicle.unladen_weight || '',
                capacity_in_unit: vehicle.capacity_in_unit || '',
                capacity_in_weight: vehicle.capacity_in_weight || '',
                capacity_in_volume: vehicle.capacity_in_volume || '',
                chasis_number: vehicle.chasis_number || '',
                engine_number: vehicle.engine_number || '',
                marker_name: vehicle.marker_name || '',
                registration_number: vehicle.registration_number || '',
                puc_validity: vehicle.puc_validity || '',
                insurance_validity: vehicle.insurance_validity || '',
                ownership: vehicle.ownership || '',
                rented_from: vehicle.rented_from || '',
                rented_to: vehicle.rented_to || '',
                owner_name: vehicle.owner_name || '',
                transporter: vehicle.transporter || '',
                financer: vehicle.financer || '',
            });
        } else {
            setFormData({
                branch_id: 0,
                vehicle_number: '',
                type_of_vehicle: '',
                make: '',
                model: '',
                unladen_weight: '',
                capacity_in_unit: '',
                capacity_in_weight: '',
                capacity_in_volume: '',
                chasis_number: '',
                engine_number: '',
                marker_name: '',
                registration_number: '',
                puc_validity: '',
                insurance_validity: '',
                ownership: '',
                rented_from: '',
                rented_to: '',
                owner_name: '',
                transporter: '',
                financer: '',
            });
        }
        setFormErrors({});
    }, [vehicle]);

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.branch_id) {
            errors.branch_id = 'Branch is required';
        }

        if (!formData.vehicle_number.trim()) {
            errors.vehicle_number = 'Vehicle number is required';
        }

        if (formData.puc_validity && !isValidDate(formData.puc_validity)) {
            errors.puc_validity = 'Invalid date format';
        }

        if (formData.insurance_validity && !isValidDate(formData.insurance_validity)) {
            errors.insurance_validity = 'Invalid date format';
        }

        if (formData.rented_from && !isValidDate(formData.rented_from)) {
            errors.rented_from = 'Invalid date format';
        }

        if (formData.rented_to && !isValidDate(formData.rented_to)) {
            errors.rented_to = 'Invalid date format';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const isValidDate = (dateString: string): boolean => {
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime());
    };

    const handleInputChange = (field: keyof CreateVehicleData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            if (vehicle) {
                // Update existing vehicle
                const updateData: UpdateVehicleData = { ...formData };
                await dispatch(updateVehicle({ id: vehicle.id, data: updateData })).unwrap();
            } else {
                // Create new vehicle
                await dispatch(createVehicle(formData)).unwrap();
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving vehicle:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <GenericModal
            open={open}
            onClose={handleClose}
            title={vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            actions={
                <>
                    <GenericButton
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        fullWidth={isMobile}
                    >
                        Cancel
                    </GenericButton>
                    <GenericButton
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || loading}
                        fullWidth={isMobile}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            vehicle ? 'Update Vehicle' : 'Create Vehicle'
                        )}
                    </GenericButton>
                </>
            }
        >
            <form onSubmit={handleSubmit}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Basic Information */}
                <Typography variant="h6" gutterBottom>
                    Basic Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth error={!!formErrors.branch_id}>
                            <InputLabel>Branch *</InputLabel>
                            <Select
                                value={formData.branch_id}
                                onChange={(e) => handleInputChange('branch_id', e.target.value)}
                                label="Branch *"
                            >
                                {branches.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.branch_id && (
                                <Typography variant="caption" color="error">
                                    {formErrors.branch_id}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Vehicle Number *"
                            value={formData.vehicle_number}
                            onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                            error={!!formErrors.vehicle_number}
                            helperText={formErrors.vehicle_number}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Vehicle Type</InputLabel>
                            <Select
                                value={formData.type_of_vehicle}
                                onChange={(e) => handleInputChange('type_of_vehicle', e.target.value)}
                                label="Vehicle Type"
                            >
                                <MenuItem value="">Select Type</MenuItem>
                                <MenuItem value="4 Wheeler">4 Wheeler</MenuItem>
                                <MenuItem value="2 Wheeler">2 Wheeler</MenuItem>
                                <MenuItem value="Truck">Truck</MenuItem>
                                <MenuItem value="Van">Van</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Make"
                            value={formData.make}
                            onChange={(e) => handleInputChange('make', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Model"
                            value={formData.model}
                            onChange={(e) => handleInputChange('model', e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Capacity Information */}
                <Typography variant="h6" gutterBottom>
                    Capacity Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Unladen Weight"
                            value={formData.unladen_weight}
                            onChange={(e) => handleInputChange('unladen_weight', e.target.value)}
                            placeholder="e.g., 2000kg"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Capacity (Units)"
                            value={formData.capacity_in_unit}
                            onChange={(e) => handleInputChange('capacity_in_unit', e.target.value)}
                            placeholder="e.g., 100"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Capacity (Weight)"
                            value={formData.capacity_in_weight}
                            onChange={(e) => handleInputChange('capacity_in_weight', e.target.value)}
                            placeholder="e.g., 5000kg"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Capacity (Volume)"
                            value={formData.capacity_in_volume}
                            onChange={(e) => handleInputChange('capacity_in_volume', e.target.value)}
                            placeholder="e.g., 50m³"
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Technical Information */}
                <Typography variant="h6" gutterBottom>
                    Technical Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Chassis Number"
                            value={formData.chasis_number}
                            onChange={(e) => handleInputChange('chasis_number', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Engine Number"
                            value={formData.engine_number}
                            onChange={(e) => handleInputChange('engine_number', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Marker Name"
                            value={formData.marker_name}
                            onChange={(e) => handleInputChange('marker_name', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Registration Number"
                            value={formData.registration_number}
                            onChange={(e) => handleInputChange('registration_number', e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Validity Information */}
                <Typography variant="h6" gutterBottom>
                    Validity Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="PUC Validity"
                            type="date"
                            value={formData.puc_validity}
                            onChange={(e) => handleInputChange('puc_validity', e.target.value)}
                            error={!!formErrors.puc_validity}
                            helperText={formErrors.puc_validity}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Insurance Validity"
                            type="date"
                            value={formData.insurance_validity}
                            onChange={(e) => handleInputChange('insurance_validity', e.target.value)}
                            error={!!formErrors.insurance_validity}
                            helperText={formErrors.insurance_validity}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Ownership Information */}
                <Typography variant="h6" gutterBottom>
                    Ownership Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Ownership</InputLabel>
                            <Select
                                value={formData.ownership}
                                onChange={(e) => handleInputChange('ownership', e.target.value)}
                                label="Ownership"
                            >
                                <MenuItem value="">Select Ownership</MenuItem>
                                <MenuItem value="Company">Company</MenuItem>
                                <MenuItem value="Rented">Rented</MenuItem>
                                <MenuItem value="Private">Private</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Rented From"
                            type="date"
                            value={formData.rented_from}
                            onChange={(e) => handleInputChange('rented_from', e.target.value)}
                            error={!!formErrors.rented_from}
                            helperText={formErrors.rented_from}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Rented To"
                            type="date"
                            value={formData.rented_to}
                            onChange={(e) => handleInputChange('rented_to', e.target.value)}
                            error={!!formErrors.rented_to}
                            helperText={formErrors.rented_to}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Owner Name"
                            value={formData.owner_name}
                            onChange={(e) => handleInputChange('owner_name', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Transporter"
                            value={formData.transporter}
                            onChange={(e) => handleInputChange('transporter', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Financer"
                            value={formData.financer}
                            onChange={(e) => handleInputChange('financer', e.target.value)}
                        />
                    </Grid>
                </Grid>

            </form>
        </GenericModal>
    );
};

export default VehicleForm;
